import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Ban, CheckCircle2, FileText, Loader2, ShieldAlert } from "lucide-react";
import { http } from "@/lib/api";
import { billsRead, isFeatureOff } from "@/lib/billsNative";
import { fmtMoney, fmtQty } from "@/lib/billsLanguage";
import { fmtDate } from "@/components/bills/BillListCard";
import { CorrectionHistory, LineCorrection } from "@/components/bills/LineCorrection";
import { FinancialSummary } from "@/components/bills/FinancialSummary";
import { LandedCostPanel } from "@/components/bills/LandedCostPanel";
import { CONFIRM_ERRORS } from "@/lib/billsLanguage";
import { ApproveVoidSheet, RejectVoidSheet, RequestVoidSheet, VoidAction, VoidGovernanceCard, voidErrorText } from "@/components/bills/VoidBill";

const CORR_ERRORS = { reason_required: "Please add a reason for the correction.", ingredient_not_active: "That ingredient isn't available.",
  invalid_source_correction: "Enter a valid quantity or unit.", workflow_rejected: "That value doesn't fit this ingredient. Please check it." };

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[#E4E0D8] last:border-0">
    <dt className="text-[13px] text-archival-secondary">{label}</dt>
    <dd className="text-[15px] text-archival-primary text-right break-words" data-testid={testId}>{value ?? "—"}</dd>
  </div>
);

const Line = ({ ln, canCorrect, open, busy, error, onOpen, onSubmit, onCancel }) => (
  <li className={`rounded-md border bg-white p-4 ${ln.needs_information ? "border-amber-300 border-l-4" : "border-[#E4E0D8]"}`} data-testid={`detail-line-${ln.line_index}`}>
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-[16px] font-semibold text-archival-primary">{ln.description}</p>
      <p className="text-[15px] tabular-nums shrink-0">{fmtMoney(ln.line_total)}</p>
    </div>
    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]">
      <div><dt className="text-archival-secondary">Purchased</dt>
        <dd className="text-[15px] text-archival-primary" data-testid={`detail-purchased-${ln.line_index}`}>{fmtQty(ln.purchased_quantity)} {ln.purchased_unit}</dd></div>
      <div><dt className="text-archival-secondary">Inventory quantity</dt>
        <dd className="text-[15px] text-archival-primary" data-testid={`detail-inventory-${ln.line_index}`}>
          {ln.inventory_quantity != null ? `${fmtQty(ln.inventory_quantity)} ${ln.inventory_unit}` : "—"}</dd></div>
      <div className="col-span-2"><dt className="text-archival-secondary">Ingredient</dt>
        <dd className="text-[15px] text-archival-primary" data-testid={`detail-ingredient-${ln.line_index}`}>{ln.ingredient_name || "Not mapped"}</dd></div>
      <div className="col-span-2"><dt className="text-archival-secondary">How we got the quantity</dt>
        <dd className="text-[14px] text-archival-primary" data-testid={`detail-conversion-${ln.line_index}`}>{ln.conversion}</dd></div>
      {ln.mapping_source === "LEARNED_AUTO" && <p className="col-span-2 text-[12px] text-archival-muted" data-testid={`detail-learned-${ln.line_index}`}>Mapped automatically · learned from previous bills</p>}
    </dl>
    <CorrectionHistory ln={ln} />
    {canCorrect && !open && (
      <button type="button" onClick={onOpen} data-testid={`detail-correct-${ln.line_index}`}
        className="mt-2 min-h-[44px] px-1 text-[14px] font-medium text-[var(--action)] underline-offset-2 hover:underline">
        {ln.needs_information ? "Add missing information" : "Change"}</button>
    )}
    {open && <LineCorrection ln={ln} busy={busy} error={error} onSubmit={onSubmit} onCancel={onCancel} />}
  </li>
);

// Opens the EXISTING BC attachment (object storage via the BC route) — no copy, no EC call.
const openSource = async (cid, setErr) => {
  try {
    const r = await http.get(`/bulk-capture/attachment/${encodeURIComponent(cid)}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank", "noopener");
  } catch { setErr("The original bill document isn't available."); }
};

export default function BillDetail() {
  const { billId } = useParams();
  const [st, setSt] = useState({ phase: "loading", d: null });
  const [docErr, setDocErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cBusy, setCBusy] = useState(false);
  const [cErr, setCErr] = useState(null);
  const [voidSheet, setVoidSheet] = useState(null); // "request" | "approve" | "reject"
  const [vBusy, setVBusy] = useState(false);
  const [vErr, setVErr] = useState(null);
  // Governance lives in the backend: the UI only calls it and then re-reads canonical state.
  const voidCall = async (fn, body) => {
    setVBusy(true); setVErr(null);
    const r = await fn(billId, body, crypto.randomUUID());
    setVBusy(false);
    if (!r.ok) return setVErr(voidErrorText(r));
    setVoidSheet(null);
    const fresh = await billsRead.detail(billId);
    if (fresh.ok) setSt({ phase: "ready", d: fresh.data });
  };
  const submit = async (body, key) => {
    setCBusy(true); setCErr(null);
    const r = await billsRead.correct(billId, body, key);
    setCBusy(false);
    if (r.ok) { setSt({ phase: "ready", d: r.data }); return setEditing(null); }
    if (r.data?.error_code === "STALE_CORRECTION_STATE") {
      const fresh = await billsRead.detail(billId);
      if (fresh.ok) setSt({ phase: "ready", d: fresh.data });
      setEditing(null);
      return setCErr("This item changed while you were editing it. Review the latest value and try again.");
    }
    setCErr(r.status === 403 ? "You don't have permission to correct bills." : CORR_ERRORS[r.data?.error_code] || CONFIRM_ERRORS[r.data?.error_code] || r.data?.message || "That correction didn't save. Please try again.");
  };
  useEffect(() => {
    billsRead.detail(billId).then((r) => setSt(
      r.ok ? { phase: "ready", d: r.data } : { phase: isFeatureOff(r) ? "off" : r.status === 404 ? "notfound" : "unavailable", d: null }));
  }, [billId]);
  const d = st.d;
  return (
    <div className="min-h-screen bg-archival-base" data-testid="bill-detail">
      <div className="max-w-[640px] mx-auto px-4 pb-10">
        <Link to="/bills?view=confirmed" className="inline-flex items-center gap-1 min-h-[44px] text-[14px] text-archival-secondary" data-testid="bill-detail-back"><ArrowLeft size={16} />Bills</Link>
        {st.phase === "loading" && <p className="mt-6 flex items-center gap-2 text-archival-secondary" data-testid="bill-detail-loading"><Loader2 size={16} className="animate-spin" />Loading bill…</p>}
        {st.phase === "notfound" && <p className="mt-6 text-[15px]" data-testid="bill-detail-notfound">This bill isn't available.</p>}
        {st.phase === "off" && <p className="mt-6 text-[15px]" data-testid="bill-detail-off">Bills isn't turned on for this account yet.</p>}
        {st.phase === "unavailable" && <p role="alert" className="mt-6 text-[15px]" data-testid="bill-detail-unavailable">Bills is temporarily unavailable. Please try again shortly.</p>}
        {d && (
          <>
            <header className="mt-2">
              <p className={`inline-flex items-center gap-1 text-[13px] font-medium ${d.status === "VOIDED" || d.status === "VOID_PENDING" ? "text-red-800" : "text-[#2f5d3a]"}`} data-testid="bill-detail-status">
                {d.status === "VOIDED" ? <><Ban size={15} />VOIDED</>
                  : d.status === "VOID_PENDING" ? <><ShieldAlert size={15} />Void pending</>
                  : d.status === "CONFIRMED" ? <><CheckCircle2 size={15} />Confirmed</> : "Draft"}</p>
              <h1 className="text-[22px] font-semibold text-archival-primary" data-testid="bill-detail-vendor">{d.vendor_name || "Unknown vendor"}</h1>
            </header>
            <dl className="mt-3 rounded-md border border-[#E4E0D8] bg-white px-4">
              <Row label="Bill number" value={d.bill_number} testId="bill-detail-number" />
              <Row label="Bill date" value={fmtDate(d.bill_date)} testId="bill-detail-date" />
              {!d.financials?.rows?.length && <Row label="Total" value={fmtMoney(d.total_amount)} testId="bill-detail-total" />}
            </dl>
            {/* GST / VAT / TCS / delivery kept apart, only where the document carried them */}
            <div className="mt-3"><FinancialSummary fin={d.financials} testId="bill-detail-financials" totalTestId="bill-detail-total" /></div>
            <div className="mt-3"><LandedCostPanel landed={d.landed_cost} currency={d.currency} testId="bill-detail-landed-cost" /></div>
            {d.source_document.available ? (
              <button type="button" onClick={() => openSource(d.source_document.candidate_id, setDocErr)} data-testid="bill-detail-source"
                className="mt-3 w-full h-12 rounded-md border border-[#E4E0D8] bg-white text-[15px] inline-flex items-center justify-center gap-2"><FileText size={16} />View original bill</button>
            ) : <p className="mt-3 text-[13px] text-archival-secondary" data-testid="bill-detail-source-missing">Original bill document not attached.</p>}
            {docErr && <p role="alert" className="mt-2 text-[13px] text-red-800">{docErr}</p>}
            <h2 className="mt-6 text-[15px] font-semibold text-archival-primary">Items ({d.lines.length})</h2>
            {cErr && editing === null && <p role="alert" className="mt-3 text-[14px] rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="bill-detail-correction-stale">{cErr}</p>}
            {d.correction_pending && <p role="alert" className="mt-3 text-[14px] rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="bill-detail-correction-pending">A correction needs information before Inventory can be updated.</p>}
            <ul className="mt-2 space-y-2">{d.lines.map((ln) => (
              <Line key={ln.line_index} ln={ln} canCorrect={d.can_correct} open={editing === ln.line_index} busy={cBusy} error={editing === ln.line_index ? cErr : null}
                onOpen={() => { setEditing(ln.line_index); setCErr(null); }} onSubmit={submit} onCancel={() => setEditing(null)} />
            ))}</ul>
            {(d.status === "VOID_PENDING" || d.status === "VOIDED") && (
              <VoidGovernanceCard d={d} busy={vBusy} error={voidSheet ? null : vErr}
                onApprove={() => { setVErr(null); setVoidSheet("approve"); }}
                onReject={() => { setVErr(null); setVoidSheet("reject"); }} />
            )}
            <h2 className="mt-6 text-[15px] font-semibold text-archival-primary">History</h2>
            <ol className="mt-2 space-y-2" data-testid="bill-detail-history">
              {d.history.map((h, i) => (
                <li key={i} className="rounded-md bg-white border border-[#E4E0D8] px-4 py-3 text-[14px]" data-testid={`bill-history-${h.event}`}>
                  <p className="font-medium text-archival-primary">{h.label}</p>
                  <p className="text-[12px] text-archival-secondary">{h.ts ? new Date(h.ts).toLocaleString("en-IN") : ""}{h.actor ? ` · ${h.actor}` : ""}</p>
                  {h.reason && <p className="mt-1 text-[13px]">Reason: {h.reason}</p>}
                </li>
              ))}
            </ol>
            {d.status === "CONFIRMED" && d.can_request_void && <VoidAction onOpen={() => { setVErr(null); setVoidSheet("request"); }} />}
            {voidSheet === "request" && (
              <RequestVoidSheet d={d} busy={vBusy} error={vErr} onCancel={() => setVoidSheet(null)}
                onSubmit={(reason) => voidCall(billsRead.requestVoid, { reason, reason_code: "OPERATOR_VOID" })} />
            )}
            {voidSheet === "approve" && (
              <ApproveVoidSheet d={d} busy={vBusy} error={vErr} onCancel={() => setVoidSheet(null)}
                onSubmit={() => voidCall(billsRead.approveVoid, { decision_reason: "Approved on bill detail" })} />
            )}
            {voidSheet === "reject" && (
              <RejectVoidSheet busy={vBusy} error={vErr} onCancel={() => setVoidSheet(null)}
                onSubmit={(reason) => voidCall(billsRead.rejectVoid, { decision_reason: reason || "Rejected on bill detail" })} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
